import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "ar", name: "العربية" },
  { code: "pt", name: "Português" },
  { code: "zh", name: "中文" },
];

interface LanguageSwitcherProps {
  className?: string;
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
}

export const LanguageSwitcher = ({ 
  className,
  position = "top-right"
}: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get current language
  const currentLanguage = LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem("app_language", code);
    setIsOpen(false);
  };

  // Check if we should use relative positioning (when className includes "relative")
  const useRelative = className?.includes("relative");
  
  // Position classes - fixed positioning for standalone, relative for inline use
  const positionClasses = useRelative 
    ? {
        "top-right": "relative z-50",
        "top-left": "relative z-50",
        "bottom-right": "relative z-50",
        "bottom-left": "relative z-50",
      }
    : {
        "top-right": "fixed top-4 right-4 z-50",
        "top-left": "fixed top-4 left-4 z-50",
        "bottom-right": "fixed bottom-4 right-4 z-50",
        "bottom-left": "fixed bottom-4 left-4 z-50",
      };

  return (
    <div 
      ref={dropdownRef} 
      className={cn(
        positionClasses[position],
        className
      )}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg",
          "text-sm text-[hsl(var(--romantic-muted-foreground))]",
          "hover:bg-[hsl(var(--romantic-background))]/50",
          "bg-card/80 backdrop-blur-sm",
          "border border-border/50",
          "transition-colors shadow-sm",
          "whitespace-nowrap"
        )}
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4 flex-shrink-0" />
        <span className="hidden sm:inline">{currentLanguage.name}</span>
        <ChevronDown 
          className={cn(
            "w-4 h-4 transition-transform flex-shrink-0",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute mt-2 w-48 rounded-xl shadow-lg",
            "bg-card border border-border",
            "overflow-hidden z-50 animate-fade-in",
            position === "top-right" && "right-0",
            position === "top-left" && "left-0",
            position === "bottom-right" && "right-0 bottom-full mb-2",
            position === "bottom-left" && "left-0 bottom-full mb-2"
          )}
          style={{
            maxWidth: "calc(100vw - 2rem)", // Prevent overflow on mobile
          }}
        >
          <div className="max-h-64 overflow-y-auto">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-2.5 text-left",
                  "hover:bg-[hsl(var(--romantic-primary))]/10",
                  "transition-colors",
                  i18n.language === lang.code && "bg-[hsl(var(--romantic-primary))]/5"
                )}
              >
                <span className="text-sm text-foreground">{lang.name}</span>
                {i18n.language === lang.code && (
                  <Check className="w-4 h-4 text-[hsl(var(--romantic-primary))]" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

